import { debounce } from 'lodash';
import { useEffect, useState, RefObject } from 'react';
import { StylesConfig } from 'react-select';
import { useMedia } from '../../components/grid/hooks';
import { TGetStorageItemsFilterKey, TStorageItemSummary } from '../../../types/storageTypes';
import { AspectRatio } from './types';
import { getValueFromReactSelectOption } from '../../components/presentational/select/utils';

export const useResponsiveStorageItemsList = (listWrapperRef: RefObject<HTMLDivElement | null>) => {
  const [wrapperSizes, setWrapperSizes] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleResize = () => {
      if (listWrapperRef.current) {
        setWrapperSizes({
          width: listWrapperRef.current.offsetWidth,
          height: listWrapperRef.current.offsetHeight
        });
      }
    };

    handleResize();

    const debouncedHandleResize = debounce(handleResize, 100);
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [listWrapperRef.current]);

  const colsPerRow = useMedia(
    ['(min-width: 1600px', '(min-width: 1200px)', '(min-width: 600px)', '(min-width: 0px)'],
    [6, 4, 3, 2],
    1
  );

  const columnWidth = wrapperSizes.width / colsPerRow;
  const rowHeight = columnWidth;
  const totalRows = Math.max(Math.floor(wrapperSizes.height / rowHeight), 1);
  const totalColumns = totalRows * colsPerRow;

  return { totalColumns, colsPerRow };
};

export const MULTI_PARAM_SEPARATOR = ',';

export const normalizeTag = (tag: string) => {
  while (tag.includes(MULTI_PARAM_SEPARATOR)) {
    tag = tag.replace(MULTI_PARAM_SEPARATOR, '');
  }
  return tag.slice(0, 32);
};

export const parseFileName = (fileName: string) => {
  const [fileExtension, ...fileNameParts] = fileName.split('.').reverse();
  return { fileExtension, fileName: fileNameParts.reverse().join('.') };
};

export const getHumanReadableSize = (sizeInBytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let i = 0;
  let size = sizeInBytes;
  while (size >= 1024) {
    size /= 1024;
    i++;
  }
  return `${Math.round(size * 100) / 100} ${units[i]}`;
};

export const getTagColor = (tag: string) => {
  const colors = [
    '#f44336',
    '#e91e63',
    '#9c27b0',
    '#673ab7',
    '#3f51b5',
    '#2196f3',
    '#03a9f4',
    '#00bcd4',
    '#009688',
    '#4caf50',
    '#8bc34a',
    '#cddc39',
    '#ffeb3b',
    '#ffc107',
    '#ff9800',
    '#ff5722',
    '#795548',
    '#9e9e9e',
    '#607d8b'
  ];

  const charCodeSum = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

  return colors[charCodeSum % colors.length];
};

export const tagSelectStyles: StylesConfig = {
  multiValueLabel: (base, { data }: any) => {
    const value = getValueFromReactSelectOption(data);
    return {
      ...base,
      backgroundColor: `${getTagColor(value)} !important`,
      color: '#fff !important',
      paddingRight: 6,
      fontWeight: 700
    };
  },
  option: (styles, { data }: any) => {
    const value = getValueFromReactSelectOption(data);
    return { ...styles, ...dot(getTagColor(value)) };
  }
};

const dot = (color = 'transparent') => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10
  }
});

export const uploadFileToStorage = async (file: File): Promise<TStorageItemSummary | string> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', '');

    const result = await fetch('/api/storage', {
      method: 'POST',
      body: formData
    });

    const json = await result.json();

    if (!result.ok) {
      throw new Error(json.message);
    }

    return json;
  } catch (error) {
    if (error.message.includes('Payload content length greater than maximum allowed')) {
      const allowedFileSize = error.message.match(/(\d+)/g)?.[0];
      const humanReadableFileSize = getHumanReadableSize(Number(allowedFileSize));

      return `The file ${file.name} is too large. Maximum allowed file size is ${humanReadableFileSize}`;
    }

    return error.message;
  }
};

export const isValidAspectRatio = (aspectValue: string): aspectValue is AspectRatio => {
  return /^\d+[:x/]\d+$/.test(aspectValue);
};

export const parseAspectValue = (aspectValue: AspectRatio) => {
  const [width, height] = aspectValue.split(/[/:x]/).map(Number);

  return {
    width,
    height,
    ratio: width / height
  } as const;
};

export const STORAGE_ITEM_FILTER_KEYS: TGetStorageItemsFilterKey[] = [
  'tags',
  'search',
  'mimeType',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'minAspectRatio',
  'maxAspectRatio'
];
